import { useState } from 'react';
import CustomDropdown from '../forms/CustomDropdown';
import { EMPLOYMENT_TYPES, INVITE_ROLES, parseEmails } from '../../utils/onboarding';
import styles from './Onboarding.module.css';

const emptyRow = () => ({ id: Math.random(), email: '', employment_type: 'permanent', role: 'member' });
const isEmail = (e) => /^\S+@\S+\.\S+$/.test(e);

export default function TeamStep({ onInvite, onSkip, onBack }) {
  const [rows, setRows] = useState([emptyRow()]);
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const update = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const filled = rows.filter((r) => r.email.trim());

  const addPasted = () => {
    const emails = parseEmails(pasted);
    setRows((rs) => [...rs.filter((r) => r.email.trim()), ...emails.map((email) => ({ ...emptyRow(), email }))]);
    setPasted('');
    setPasting(false);
  };

  const submit = async () => {
    if (filled.some((r) => !isEmail(r.email.trim()))) return setError('Check the email addresses highlighted above.');
    setBusy(true);
    const result = await onInvite(filled.map(({ email, employment_type, role }) => ({ email: email.trim(), employment_type, role })));
    if (result?.skipped?.length) {
      const skippedEmails = result.skipped.map((s) => s.email);
      setRows(rows.filter((r) => skippedEmails.includes(r.email.trim())));
      setError(result.skipped.map((s) => `${s.email}: ${s.reason}`).join('. '));
    } else if (result?.error) {
      setError(result.error);
    }
    setBusy(false);
  };

  return (
    <div>
      <div className={styles.sectionLabel}>People <span className={styles.badge}>{filled.length}</span></div>
      {error && <div className={styles.error} role="alert">{error}</div>}

      {rows.map((r) => (
        <div key={r.id} className={styles.teamRow}>
          <input
            type="email"
            placeholder="name@company.co.za"
            aria-label="Email"
            value={r.email}
            aria-invalid={r.email.trim() && !isEmail(r.email.trim()) ? 'true' : undefined}
            style={r.email.trim() && !isEmail(r.email.trim()) ? { borderColor: '#ef4444' } : undefined}
            onChange={(e) => { update(r.id, { email: e.target.value }); setError(''); }}
          />
          <div className={r.employment_type === 'contractor' ? styles.typeContractor : undefined}>
            <CustomDropdown fullWidth compact value={r.employment_type} onChange={(v) => update(r.id, { employment_type: v })} options={EMPLOYMENT_TYPES} />
          </div>
          <CustomDropdown fullWidth compact value={r.role} onChange={(v) => update(r.id, { role: v })} options={INVITE_ROLES} />
          <button type="button" className={styles.iconButton} aria-label="Remove" onClick={() => setRows((rs) => (rs.length > 1 ? rs.filter((x) => x.id !== r.id) : [emptyRow()]))}>×</button>
        </div>
      ))}

      <div className={styles.linkRow}>
        <button type="button" className={styles.link} onClick={() => setRows((rs) => [...rs, emptyRow()])}>+ Add another</button>
        <button type="button" className={styles.link} onClick={() => setPasting((p) => !p)}>Paste a list of emails</button>
      </div>

      {pasting && (
        <div>
          <textarea className={styles.pasteArea} placeholder="One per line, or separated by commas" value={pasted} onChange={(e) => setPasted(e.target.value)} />
          <button type="button" className={styles.link} onClick={addPasted}>Add these emails</button>
        </div>
      )}

      <div className={styles.infoBox}>
        <strong>{filled.length ? `${filled.length} invite${filled.length === 1 ? '' : 's'} will be sent` : 'No invites yet. You can invite people later from People.'}</strong>
        You're the owner and admin.
      </div>
      <p className={styles.note}>Vehicles, tools and rooms can be added later in People &amp; Resources.</p>

      <div className={styles.actions}>
        <button type="button" className={styles.link} onClick={onBack}>← Back</button>
        <div className={styles.actionsRight}>
          <button type="button" className={styles.link} onClick={onSkip}>Skip for now</button>
          <button type="button" className={styles.primaryButton} onClick={filled.length ? submit : onSkip} disabled={busy}>
            {busy ? 'Sending…' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
